<template>
  <div class="relative w-full h-full">
    <div
      v-if="editMode || highlighted"
      class="absolute z-40 w-full h-full rounded-lg bg-blue opacity-20"
      :class="{ 'animate-pulse-bg from-bg-blue-900 to-bg-blue-50': highlighted }" />

    <template v-if="item?.props?.component_type == ALL_COMPONENTS.OBJECT_TABLE">
      <ObjectView
        :key="item.id"
        :fixed="item.fixed"
        :data-sources="dataSources"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w"
        @update:preserved-filters="($event) => updatePreservedFilters($event, item.id)"
        @dbclick="highlightDataSource" />
    </template>

    <template
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.ROLLUP_METRIC || item?.props?.component_type == ALL_COMPONENTS.SCORE">
      <RollUpMetric
        v-if="
          $utils.getMetricById(item.props?.component_props.rollup ? item.props?.component_props.rollup : item.props?.component_props.score)
        "
        v-bind="item.props?.component_props"
        :metric="{
          ...$utils.getMetricById(
            item.props?.component_props.rollup ? item.props?.component_props.rollup : item.props?.component_props.score
          ),
          value: null
        }"
        :component-reference="item.props?.component_reference"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        :item-layout-width="item.w"
        :item-layout-height="item.h"
        :show-description="item.props?.component_props?.showDescription"
        @update:preview-selections="($event) => updateRollupPreviewProps($event, item.id)"
        @dblclick="highlightDataSource" />
      <div v-else class="flex items-center justify-center w-full h-full bg-white rounded-lg dark:bg-dark-screen-background">
        {{ $t("Deleted Metric") }}
      </div>
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.FORECASTING_METRIC">
      <ForecastingMetric
        v-bind="item.props?.component_props"
        :current-template="currentTemplate"
        :metric-name="item.props?.component_props.metricName"
        :component-reference="item.props?.component_reference"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        :item-layout-width="item.w"
        @dblclick="highlightDataSource" />
    </template>

    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.GRAPH">
      <Graph
        class="w-full h-full"
        :page-reference="pageReference"
        v-bind="item.props?.component_props"
        :item-layout-height="item.h"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.CALENDAR">
      <CalendarView
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.JOURNEY">
      <CustomerJourneysView
        class="relative w-full h-full z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item.props?.component_type == ALL_COMPONENTS.SUGGESTED_LIST">
      <SuggestedListView
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item.props?.component_type == ALL_COMPONENTS.GROWTH_MATRIX">
      <GrowthMatrixView
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.ACTIONS_TABLE">
      <ActionsAndInteractionsTable
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w"
        :current-template="currentTemplate"
        @update:preserved-filters="($event) => updatePreservedFilters($event, item.id)" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.STANDARD_METRICS">
      <StandardMetricsComponent
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.SEND_EMAIL">
      <SendEmailComponent
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.NEW_RESOURCES">
      <NewResourcesComponent
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.TEAM_CHAT">
      <TeamChat
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.WHATSAPP_CHAT">
      <WhatsappChat
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.MILESTONE">
      <Milestone
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.LEADS_TABLE">
      <LeadsTable
        class="relative z-1"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        @update:preserved-filters="($event) => updatePreservedFilters($event, item.id)" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.FORECASTING_TABLE">
      <DashboardForecastingTable
        class="bg-white"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference"
        :item-layout-width="item.w"
        @dblclick="highlightDataSource" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.EXPANSION_LIST_TABLE">
      <ExpansionLists
        class="bg-white"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.FIND_LEADS">
      <FindLeads
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>
    <template v-else-if="item?.props?.component_type == ALL_COMPONENTS.CUSTOMER_JOURNEYS">
      <CustomerJourneys
        class="bg-white"
        :current-template="currentTemplate"
        :page-reference="pageReference"
        :current-company="$store.state.ui.currentCompany"
        v-bind="item.props?.component_props"
        :component-reference="item.props?.component_reference" />
    </template>

    <CompanyInsightsComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.COMPANY_PROFILE_COMPANY_INSIGHTS"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <CompanyMapComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.COMPANY_MAP"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <ForecastChecklistComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.CHECKLIST"
      :width="item.w"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <CompanyOverviewCompanyDetails
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.OVERVIEW"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <TextComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.TEXT"
      :id="item.id"
      :item="item"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :current-template="currentTemplate"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <NotesComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.NOTES"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :current-template="currentTemplate"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <DataSourceComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.DATA_SOURCE"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :current-template="currentTemplate"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <ImageComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.IMAGE"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :page-reference="pageReference"
      :current-template="currentTemplate"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <VideoComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.VIDEO"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :current-company="$store.state.ui.currentCompany"
      :page-reference="pageReference"
      :current-template="currentTemplate"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <AssetsComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.ASSETS"
      :id="item.id"
      :current-company="$store.state.ui.currentCompany"
      :current-template="currentTemplate"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <AIInsights
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.AI_INSIGHTS"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      :current-template="currentTemplate"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <AIQuickActions
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.AI_QUICK_ACTIONS"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      :current-template="currentTemplate"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <TeamCardComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.TEAM_CARD"
      :id="item.id"
      :w="item.w"
      :h="item.h"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-0 rounded-lg" />

    <RecordTitleComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.RECORD_TITLE"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :current-template="currentTemplate"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <InsightsComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.INSIGHTS"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-6 bg-white rounded-lg" />

    <OwnersListComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.OWNERS"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <KeyPeopleComponent
      v-else-if="item?.props?.component_type == ALL_COMPONENTS.KEY_PEOPLE"
      :id="item.id"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <LastTouchComponent
      v-else-if="
        item?.props?.component_type == ALL_COMPONENTS.LAST_QUALITY_TOUCH || item?.props?.component_type == ALL_COMPONENTS.LAST_TOUCH
      "
      :id="item.id"
      :component-type="item?.props?.component_type"
      :component-reference="item?.props?.component_reference"
      :page-reference="pageReference"
      :current-company="$store.state.ui.currentCompany"
      v-bind="item?.props?.component_props"
      class="w-full h-full p-4 bg-white rounded-lg" />

    <div v-else class="w-full h-full">
      <div class="flex items-center justify-center w-full h-full text-xl text-white capitalize rounded-md text-bold bg-blue bg-opacity-80">
        {{ item?.props?.component_type }} {{ $t("Goes here component not ready") }} :(
      </div>
    </div>
  </div>
</template>

<script>
import { ALL_COMPONENTS } from "@/store/constants/all_components"
import Graph from "@/components/shared/graphs/Graph.vue"
import ObjectView from "@/components/shared/object-view/ObjectView.vue"

import RollUpMetric from "@/components/shared/rollup-metrics/RollUpMetric.vue"
import ForecastingMetric from "@/components/shared/rollup-metrics/ForecastingMetric.vue"
import CalendarView from "@/views/dashboard/dashboard/CalendarView/index.vue"
import CustomerJourneysView from "@/components/shared/CustomerJourneysView.vue"
import SuggestedListView from "@/components/shared/SuggestedListView.vue"
import GrowthMatrixView from "@/components/shared/GrowthMatrixView.vue"
import ActionsAndInteractionsTable from "@/components/shared/ActionsAndInteractionsTable.vue"
import StandardMetricsComponent from "@/components/shared/StandardMetricsComponent.vue"
import SendEmailComponent from "@/components/shared/SendEmailComponent.vue"
import NewResourcesComponent from "@/components/shared/NewResourcesComponent.vue"
import TeamChat from "@/components/shared/TeamChat.vue"
import WhatsappChat from "@/components/shared/WhatsAppChat"
import Milestone from "@/components/shared/Milestone.vue"
import DashboardForecastingTable from "@/components/shared/DashboardForecastingTable.vue"
import LeadsTable from "@/views/dashboard/dashboard/LeadsTable.vue"

import ExpansionLists from "@/components/shared/ExpansionLists.vue"
import FindLeads from "@/components/shared/FindLeads.vue"
import CustomerJourneys from "@/components/shared/CustomerJourneys.vue"

import CompanyOverviewCompanyDetails from "@/components/shared/CompanyOverviewCompanyDetails.vue"
import CompanyInsightsComponent from "@/components/shared/CompanyInsightsComponent.vue"
import CompanyMapComponent from "@/components/shared/CompanyMapComponent.vue"
import ForecastChecklistComponent from "@/components/shared/ForecastChecklistComponent.vue"

import TextComponent from "@/components/shared/TextComponent.vue"
import NotesComponent from "@/components/shared/NotesComponent.vue"
import DataSourceComponent from "@/components/shared/DataSourceComponent.vue"
import ImageComponent from "@/components/shared/ImageComponent.vue"
import VideoComponent from "@/components/shared/VideoComponent.vue"

import AssetsComponent from "@/components/shared/AssetsComponent.vue"
import TeamCardComponent from "@/components/shared/TeamCardComponent.vue"

import AIInsights from "@/components/shared/AIInsights.vue"
import AIQuickActions from "@/components/shared/AIQuickActions.vue"

import RecordTitleComponent from "@/components/shared/RecordTitleComponent.vue"
import InsightsComponent from "@/components/shared/InsightsComponent.vue"

import KeyPeopleComponent from "@/components/shared/KeyPeopleComponent.vue"
import LastTouchComponent from "@/components/shared/LastTouchComponent.vue"

import OwnersListComponent from "@/components/shared/OwnersListComponent.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    AIInsights,
    AIQuickActions,
    KeyPeopleComponent,
    LastTouchComponent,
    CompanyInsightsComponent,
    CompanyOverviewCompanyDetails,
    LeadsTable,
    ObjectView,
    RollUpMetric,
    ForecastingMetric,
    Graph,
    CalendarView,
    CustomerJourneysView,
    SuggestedListView,
    GrowthMatrixView,
    ActionsAndInteractionsTable,
    StandardMetricsComponent,
    SendEmailComponent,
    NewResourcesComponent,
    TeamChat,
    WhatsappChat,
    Milestone,
    DashboardForecastingTable,
    ExpansionLists,
    FindLeads,
    CustomerJourneys,
    CompanyMapComponent,
    ForecastChecklistComponent,
    TextComponent,
    NotesComponent,
    DataSourceComponent,
    ImageComponent,
    VideoComponent,
    AssetsComponent,
    TeamCardComponent,
    RecordTitleComponent,
    InsightsComponent,
    OwnersListComponent
  },

  inject: ["setPageLayout"],
  props: {
    currentTemplate: {
      type: Object,
      required: false
    },
    dataSources: {
      type: Object,
      required: false
    },
    item: {
      type: Object,
      required: true
    },
    pageReference: {
      type: String,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      highlighted: false,
      ALL_COMPONENTS,
      item_data: []
    }
  },
  created() {
    this.registerListeners()
  },
  unmounted() {
    this.unregisterListeners()
  },
  methods: {
    updatePreservedFilters(filters_obj, component_id) {
      const current_template_clone = this.$utils.clone(this.currentTemplate)
      const index = current_template_clone.layout.findIndex((t) => t.id == component_id)
      current_template_clone.layout.splice(index, 1, {
        ...current_template_clone.layout[index],
        props: {
          ...current_template_clone.layout[index].props,
          component_props: {
            ...current_template_clone.layout[index].props.component_props,
            ...filters_obj
          }
        }
      })

      this.setPageLayout(current_template_clone)
    },

    updateRollupPreviewProps(obj, component_id) {
      const current_template_clone = this.$utils.clone(this.currentTemplate)
      const index = current_template_clone.layout.findIndex((t) => t.id == component_id)
      current_template_clone.layout.splice(index, 1, {
        ...current_template_clone.layout[index],
        props: {
          ...current_template_clone.layout[index].props,
          component_props: {
            ...current_template_clone.layout[index].props.component_props,
            ...obj
          }
        }
      })

      this.setPageLayout(current_template_clone)
    },
    registerListeners() {
      this.$emitter.on(ALL_EMITTERS.HIGHLIGHT_DATASOURCE, ({ dataSource, highlight, trigger_reference = null }) => {
        if (
          this.getLayoutComponentProps(trigger_reference)?.component_type == ALL_COMPONENTS.ROLLUP_METRIC ||
          this.getLayoutComponentProps(trigger_reference)?.component_type == ALL_COMPONENTS.FORECASTING_METRIC ||
          this.getLayoutComponentProps(trigger_reference)?.component_type == ALL_COMPONENTS.SCORE
        ) {
          if (
            (dataSource == this.item.props?.component_props.dataSource &&
              trigger_reference == this.item.props?.component_reference &&
              (this.item.props?.component_type == ALL_COMPONENTS.ROLLUP_METRIC ||
                this.item.props?.component_type == ALL_COMPONENTS.FORECASTING_METRIC ||
                this.item.props?.component_type == ALL_COMPONENTS.SCORE)) ||
            (dataSource == this.item.props?.component_reference &&
              (this.item.props?.component_type == ALL_COMPONENTS.FORECASTING_TABLE ||
                this.item.props?.component_type == ALL_COMPONENTS.OBJECT_TABLE))
          ) {
            this.highlighted = highlight
          }
        } else {
          if (
            (trigger_reference == this.item.props?.component_props?.dataSource &&
              (this.item.props?.component_type == ALL_COMPONENTS.ROLLUP_METRIC ||
                this.item.props?.component_type == ALL_COMPONENTS.FORECASTING_METRIC ||
                this.item.props?.component_type == ALL_COMPONENTS.SCORE)) ||
            (trigger_reference == this.item.props?.component_reference &&
              (this.item.props?.component_type == ALL_COMPONENTS.FORECASTING_TABLE ||
                this.item.props?.component_type == ALL_COMPONENTS.OBJECT_TABLE))
          ) {
            this.highlighted = highlight
          }
        }
      })
      // Live-refresh: when the record this layout is showing gets updated/recomputed on the BE
      // (broadcast over the WebSocket -> RECORD.UPDATE -> emitRecordUpdatedEmitters), refresh the
      // shared store record so metric tiles bound to ui.currentCompany re-render with the fresh
      // (recomputed) values. Mirrors the live listener in CompanyOverviewCompanyDetails.vue.
      this.$emitter.on(ALL_EMITTERS.RECORD_UPDATED_IN_LAYOUT_ITEM_WRAPPER, (updated_record) => {
        if (updated_record?.id && updated_record.id == this.$store.state.ui.currentCompany?.id) {
          this.$store.commit("data/setRecord", updated_record)
          this.$store.dispatch("ui/setCurrentCompany", updated_record)
        }
      })
    },
    unregisterListeners() {
      this.$emitter.off(ALL_EMITTERS.RECORD_UPDATED_IN_LAYOUT_ITEM_WRAPPER)
      this.$emitter.off(ALL_EMITTERS.HIGHLIGHT_DATASOURCE)
    },
    getLayoutComponentProps(component_reference) {
      return this.currentTemplate.layout.find((l) => l.props?.component_reference == component_reference)?.props
    },
    highlightDataSource() {
      this.$emitter.emit(ALL_EMITTERS.HIGHLIGHT_DATASOURCE, {
        dataSource: this.item.props?.component_props.dataSource,
        trigger_reference: this.item.props?.component_reference,
        highlight: true
      })
      setTimeout(() => {
        this.$emitter.emit(ALL_EMITTERS.HIGHLIGHT_DATASOURCE, {
          dataSource: this.item.props?.component_props.dataSource,
          trigger_reference: this.item.props?.component_reference,
          highlight: false
        })
      }, 1000)
    }
  }
}
</script>
